import React, { useState, useEffect, useRef } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useShareData } from "../contexts/ShareDataContext";
import { isQuestionAuthor } from "../utils/ownershipUtils";
import {
  getProject,
  getQuestions,
  createQuestion,
  bulkCreateQuestions,
  updateProject,
  updateQuestion,
  deleteQuestion,
  createComment,
  updateComment,
  deleteComment,
  createShare,
  getCategories,
  createCategory,
  deleteCategory,
} from "../api/client";
import Modal from "../components/Modal";
import QuestionAccordion from "../components/QuestionAccordion";
import ShareModal from "../components/ShareModal";
import BulkQuestionModal from "../components/BulkQuestionModal";
import CategoryModal from "../components/CategoryModal";
import QuestionNavigation from "../components/QuestionNavigation";
import NotificationsBell from "../components/NotificationsBell";
import ErrorBoundary from "../components/ErrorBoundary";
import { exportProjectToPDF } from "../utils/pdfExport";
import { useQuestionComments } from "../hooks/useQuestionComments";
import { useQuestionRealtime } from "../hooks/useQuestionRealtime";
import { normalizeCommentHtml } from "../utils/sanitize";
import "./ProjectDetail.css";

const EditIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: "block" }}
  >
    <path
      d="M11.333 2.00004C11.5081 1.82494 11.716 1.68605 11.9447 1.59129C12.1735 1.49653 12.4187 1.44775 12.6663 1.44775C12.914 1.44775 13.1592 1.49653 13.3879 1.59129C13.6167 1.68605 13.8246 1.82494 13.9997 2.00004C14.1748 2.17513 14.3137 2.383 14.4084 2.61178C14.5032 2.84055 14.552 3.08575 14.552 3.33337C14.552 3.58099 14.5032 3.82619 14.4084 4.05497C14.3137 4.28374 14.1748 4.49161 13.9997 4.66671L5.33301 13.3334L1.99967 14L2.66634 10.6667L11.333 2.00004Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function ProjectDetail() {
  const { projectId } = useParams();
  const { user, logout } = useAuth();
  const { shareData } = useShareData();
  const navigate = useNavigate();
  const location = useLocation();

  const [project, setProject] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [authorFilter, setAuthorFilter] = useState("all");
  const [showShareModal, setShowShareModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newQuestionContent, setNewQuestionContent] = useState("");
  const [newQuestionCategory, setNewQuestionCategory] = useState("");
  const [creatingQuestion, setCreatingQuestion] = useState(false);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [editedTitle, setEditedTitle] = useState("");
  const [editedDescription, setEditedDescription] = useState("");
  const questionInputRef = useRef(null);

  // Initialize WebSocket for real-time comment updates
  const { addPendingAction: addCommentPendingAction } = useQuestionComments(
    questions,
    setQuestions,
    projectId,
  );

  // Initialize WebSocket for real-time question updates
  const { addPendingAction: addQuestionPendingAction } = useQuestionRealtime(
    questions,
    setQuestions,
    projectId,
  );

  useEffect(() => {
    fetchProjectData();
  }, [projectId]);

  useEffect(() => {
    setShowBulkActions(selectedQuestions.length > 0);
  }, [selectedQuestions]);

  // If navigated from a notification, scroll to target question/comment
  useEffect(() => {
    if (loading) return;
    const state = location.state || {};
    // Support both history state and query params to carry scroll targets
    let { scrollToQuestionId, scrollToCommentId } = state;
    if (!scrollToQuestionId && !scrollToCommentId) {
      const params = new URLSearchParams(location.search || "");
      scrollToQuestionId = params.get("scrollToQuestionId");
      scrollToCommentId = params.get("scrollToCommentId");
    }
    if (!scrollToQuestionId && !scrollToCommentId) return;

    let cancelled = false;

    const applyFlash = (element) => {
      if (!element) return;
      element.classList.remove('flash-highlight');
      // Force reflow so animation can replay if the class was previously applied
      void element.offsetWidth;
      element.classList.add('flash-highlight');
      const cleanup = () => {
        element.classList.remove('flash-highlight');
      };
      element.addEventListener('animationend', cleanup, { once: true });
    };

    const waitForElementById = (id, { timeout = 3000, interval = 50 } = {}) => {
      return new Promise((resolve) => {
        const start = Date.now();
        const check = () => {
          const node = document.getElementById(id);
          if (node) return resolve(node);
          if (Date.now() - start >= timeout) return resolve(null);
          setTimeout(check, interval);
        };
        check();
      });
    };

    const openQuestionIfNeeded = (qid, shouldHighlight = true) => {
      const container = document.getElementById(`question-${qid}`);
      if (!container) return;
      // Expanded panel is rendered only when open
      const isExpanded = !!container.querySelector('.question-expanded-content');
      if (!isExpanded) {
        const toggle = container.querySelector('.question-toggle') || container.querySelector('.question-header-content');
        if (toggle) toggle.click();
      }
      container.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (shouldHighlight) {
        applyFlash(container);
      }
    };

    const tryScrollToComment = (cid) => {
      const el = document.getElementById(`comment-${cid}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        applyFlash(el);
        return true;
      }
      return false;
    };

    const clearState = () => {
      // Remove scroll params from both history state and URL, keep other params (e.g., share_token, data)
      const params = new URLSearchParams(location.search || "");
      params.delete("scrollToQuestionId");
      params.delete("scrollToCommentId");
      navigate(
        {
          pathname: location.pathname,
          search: params.toString() ? `?${params.toString()}` : "",
        },
        { replace: true, state: {} },
      );
    };

    // If question id not provided for a comment, derive it from loaded questions
    const getQuestionIdForComment = (cid) => {
      const cidNum = Number(cid);
      const q = questions.find((q) => (q.comments || []).some((c) => Number(c.id) === cidNum));
      return q?.id;
    };

    const targetQuestionId = scrollToQuestionId || (scrollToCommentId ? getQuestionIdForComment(scrollToCommentId) : null);

    // Perform scroll/open
    if (targetQuestionId) {
      const highlightQuestion = !scrollToCommentId;
      openQuestionIfNeeded(targetQuestionId, highlightQuestion);
      // If this is a pure question target, clear state right away
      if (!scrollToCommentId) {
        clearState();
      }
    }
    if (scrollToCommentId) {
      (async () => {
        const el = await waitForElementById(`comment-${scrollToCommentId}`);
        if (!cancelled && el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          applyFlash(el);
        }
        // Clear state after attempting comment highlight (success or timeout)
        if (!cancelled) {
          clearState();
        }
      })();
    }

    return () => {
      cancelled = true;
    };
  }, [location.state, loading, navigate, questions]);

  const fetchProjectData = async () => {
    try {
      const [projectData, questionsData, categoriesData] = await Promise.all([
        getProject(projectId),
        getQuestions(projectId),
        getCategories(projectId),
      ]);
      setProject(projectData);
      setQuestions(questionsData);
      setCategories(categoriesData);
    } catch (err) {
      console.error("Failed to fetch project data:", err);
      if (err.response?.status === 403 || err.response?.status === 404) {
        navigate("/projects");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateQuestion = async (e) => {
    e.preventDefault();
    if (!newQuestionContent.trim()) return;

    setCreatingQuestion(true);

    try {
      const newQuestion = await createQuestion(projectId, {
        content: newQuestionContent,
        category_id: newQuestionCategory || undefined,
      });
      const normalizedQuestion = {
        ...newQuestion,
        status: newQuestion?.status || "untouched",
      };
      // Mark as pending before UI update to minimize race with WS
      addQuestionPendingAction("add", newQuestion.id);

      // Optimistically add question if not already present (avoid WS race duplicates)
      setQuestions((prev) => {
        if (prev.some((q) => q.id === newQuestion.id)) return prev;
        return [...prev, normalizedQuestion];
      });
      setNewQuestionContent("");
      if (newQuestionCategory) {
        setNewQuestionCategory("");
      }

      // Refocus the input to allow adding more questions
      setTimeout(() => {
        if (questionInputRef.current) {
          questionInputRef.current.focus();
        }
      }, 0);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to create question");
    } finally {
      setCreatingQuestion(false);
    }
  };

  const handleBulkCreate = async (questionsData) => {
    const createdQuestions = await bulkCreateQuestions(projectId, {
      questions: questionsData,
    });
    const normalizedQuestions = createdQuestions.map((question) => ({
      ...question,
      status: question?.status || "untouched",
    }));
    // Mark as pending before UI update
    createdQuestions.forEach((q) => addQuestionPendingAction("add", q.id));
    // Optimistically add only those not already present
    setQuestions((prev) => {
      const existingIds = new Set(prev.map((q) => q.id));
      const toAdd = normalizedQuestions.filter((q) => !existingIds.has(q.id));
      return toAdd.length ? [...prev, ...toAdd] : prev;
    });
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm("Are you sure you want to delete this question?")) {
      return;
    }

    // Mark as pending action to prevent duplicate from WebSocket
    addQuestionPendingAction("delete", questionId);

    try {
      await deleteQuestion(projectId, questionId);
      setQuestions((prev) => prev.filter((q) => q.id !== questionId));
    } catch (err) {
      alert(err.response?.data?.error || "Failed to delete question");
    }
  };

  const handleCreateComment = async (questionId, content) => {
    try {
      const safeHtml = normalizeCommentHtml(content);
      const newComment = await createComment(projectId, questionId, {
        content: safeHtml,
      });

      // Mark as pending action to prevent duplicate from WebSocket
      addCommentPendingAction("add", newComment.id);

      // Optimistically add to UI
      setQuestions((prev) =>
        prev.map((q) => {
          if (q.id !== questionId) return q;
          const existingComments = q.comments || [];
          const alreadyExists = existingComments.some((c) => c.id === newComment.id);
          const updatedComments = alreadyExists
            ? existingComments
            : [...existingComments, newComment];
          const updatedStatus = q.status === "untouched" ? "in_progress" : q.status;
          return { ...q, comments: updatedComments, status: updatedStatus };
        }),
      );
    } catch (err) {
      alert(err.response?.data?.message || "Failed to create comment");
    }
  };

  const handleMarkAsAnswer = async (questionId, commentId, isAnswer) => {
    try {
      await updateComment(projectId, questionId, commentId, {
        is_answer: isAnswer,
      });
      setQuestions((prev) =>
        prev.map((q) => {
          if (q.id === questionId) {
            const updatedComments = (q.comments || []).map((c) =>
              c.id === commentId
                ? { ...c, is_answer: isAnswer }
                : { ...c, is_answer: false },
            );
            return {
              ...q,
              comments: updatedComments,
              status: isAnswer ? "answered" : "in_progress",
            };
          }
          return q;
        }),
      );
    } catch (err) {
      alert(err.response?.data?.message || "Failed to mark as answer");
    }
  };

  const handleDeleteComment = async (questionId, commentId) => {
    if (!window.confirm("Are you sure you want to delete this comment?")) {
      return;
    }

    try {
      await deleteComment(projectId, questionId, commentId);

      // Mark as pending action to prevent duplicate from WebSocket
      addCommentPendingAction("delete", commentId);

      // Optimistically remove from UI
      setQuestions((prev) =>
        prev.map((q) =>
          q.id === questionId
            ? {
                ...q,
                comments: (q.comments || []).filter((c) => c.id !== commentId),
              }
            : q,
        ),
      );
    } catch (err) {
      alert(err.response?.data?.error || "Failed to delete comment");
    }
  };

  const handleCreateCategory = async (categoryName) => {
    try {
      const newCategory = await createCategory(projectId, {
        name: categoryName,
      });
      setCategories([...categories, newCategory]);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to create category");
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm("Are you sure you want to delete this category?")) {
      return;
    }

    try {
      await deleteCategory(projectId, categoryId);
      setCategories(categories.filter((c) => c.id !== categoryId));
    } catch (err) {
      alert(err.response?.data?.error || "Failed to delete category");
    }
  };

  const [pdfExportProgress, setPdfExportProgress] = useState(null);

  const handleExportPDF = async () => {
    if (!project) return;
    
    try {
      setPdfExportProgress(0);
      await exportProjectToPDF(project, filteredQuestions, (progress) => {
        setPdfExportProgress(progress);
      });
      setPdfExportProgress(null);
    } catch (error) {
      setPdfExportProgress(null);
      alert(`Failed to export PDF: ${error.message}`);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleToggleQuestion = (questionId) => {
    setSelectedQuestions((prev) =>
      prev.includes(questionId)
        ? prev.filter((id) => id !== questionId)
        : [...prev, questionId],
    );
  };

  const handleSelectAll = () => {
    if (selectedQuestions.length === filteredQuestions.length) {
      setSelectedQuestions([]);
    } else {
      setSelectedQuestions(filteredQuestions.map((q) => q.id));
    }
  };

  const handleBulkDelete = async () => {
    if (
      !window.confirm(
        `Are you sure you want to delete ${selectedQuestions.length} question(s)?`,
      )
    ) {
      return;
    }

    console.log('🗑️ Bulk questions deleting via API:', {
      questionIds: selectedQuestions,
      projectId: projectId,
    });

    // Mark as pending actions to prevent duplicates from WebSocket
    selectedQuestions.forEach((questionId) => {
      addQuestionPendingAction("delete", questionId);
      console.log('🔒 Marked as pending action: delete-' + questionId);
    });

    try {
      await Promise.all(
        selectedQuestions.map((questionId) =>
          deleteQuestion(projectId, questionId),
        ),
      );
      setQuestions((prev) => prev.filter((q) => !selectedQuestions.includes(q.id)));
      setSelectedQuestions([]);
    } catch (err) {
      alert("Failed to delete some questions");
    }
  };

  const handleBulkAssignCategory = async (categoryId) => {
    try {
      await Promise.all(
        selectedQuestions.map((questionId) =>
          updateQuestion(projectId, questionId, {
            category_id: categoryId || null,
          }),
        ),
      );
      setQuestions((prev) =>
        prev.map((q) =>
          selectedQuestions.includes(q.id)
            ? { ...q, category_id: categoryId || null }
            : q,
        ),
      );
      setSelectedQuestions([]);
    } catch (err) {
      alert("Failed to assign category to some questions");
    }
  };

  const handleStartEditTitle = () => {
    setEditedTitle(project.name);
    setEditingTitle(true);
  };

  const handleStartEditDescription = () => {
    setEditedDescription(project.description || "");
    setEditingDescription(true);
  };

  const handleSaveTitle = async () => {
    if (!editedTitle.trim()) {
      alert("Title cannot be empty");
      return;
    }

    try {
      await updateProject(projectId, { name: editedTitle });
      setProject({ ...project, name: editedTitle });
      setEditingTitle(false);
    } catch (err) {
      alert("Failed to update title");
    }
  };

  const handleSaveDescription = async () => {
    try {
      await updateProject(projectId, { description: editedDescription });
      setProject({ ...project, description: editedDescription });
      setEditingDescription(false);
    } catch (err) {
      alert("Failed to update description");
    }
  };

  const handleCancelEditTitle = () => {
    setEditingTitle(false);
    setEditedTitle("");
  };

  const handleCancelEditDescription = () => {
    setEditingDescription(false);
    setEditedDescription("");
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSaveTitle();
    } else if (e.key === "Escape") {
      handleCancelEditTitle();
    }
  };

  const handleDescriptionKeyDown = (e) => {
    if (e.key === "Enter" && e.ctrlKey) {
      handleSaveDescription();
    } else if (e.key === "Escape") {
      handleCancelEditDescription();
    }
  };

  const getFilteredQuestions = () => {
    let filtered = questions;

    // Apply status filter
    switch (filter) {
      case "answered":
        filtered = questions.filter((q) => q.status === "answered");
        break;
      case "unanswered":
        filtered = questions.filter((q) => q.status === "untouched");
        break;
      case "in_progress":
        filtered = questions.filter((q) => q.status === "in_progress");
        break;
      default:
        filtered = questions;
    }

    // Apply author filter
    switch (authorFilter) {
      case "myself":
        filtered = filtered.filter((q) => isQuestionAuthor(q, user, shareData));
        break;
      case "others":
        filtered = filtered.filter((q) => !isQuestionAuthor(q, user, shareData));
        break;
      default:
        // "all" - no additional filtering
        break;
    }

    // Sort by creation date, newest first (without mutating original state)
    return [...filtered].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  };

  const filteredQuestions = getFilteredQuestions();

  const getQuestionStats = (authorFilterValue = "all") => {
    // First filter questions by author
    let authorFilteredQuestions = questions;

    switch (authorFilterValue) {
      case "myself":
        authorFilteredQuestions = questions.filter((q) => isQuestionAuthor(q, user, shareData));
        break;
      case "others":
        authorFilteredQuestions = questions.filter((q) => !isQuestionAuthor(q, user, shareData));
        break;
      default:
        // "all" - no additional filtering
        break;
    }

    return {
      total: authorFilteredQuestions.length,
      answered: authorFilteredQuestions.filter((q) => q.status === "answered").length,
      inProgress: authorFilteredQuestions.filter((q) => q.status === "in_progress").length,
      untouched: authorFilteredQuestions.filter((q) => q.status === "untouched").length,
    };
  };

  const stats = getQuestionStats(authorFilter);

  if (loading) {
    return <div className="loading-container">Loading project...</div>;
  }

  if (!project) {
    return <div className="loading-container">Project not found</div>;
  }

  return (
    <div className="project-detail-page">
      <header className="header">
        <div className="container">
          <div className="header-content">
            <Link to="/projects" className="logo">
              Q&A
            </Link>
            <div className="nav">
              <NotificationsBell />
              {user && <span className="user-info">{user.name}</span>}
              {user && (
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={handleLogout}
                  aria-label="Logout"
                  type="button"
                >
                  Logout
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="project-detail-container">
        <div className="project-detail-content">
          <div className="project-header-section">
            <div className="breadcrumb">
              <Link to="/projects" className="breadcrumb-link">
                Projects
              </Link>
              <span className="breadcrumb-separator">/</span>
              <span className="breadcrumb-current">{project.name}</span>
            </div>

            <div className="project-title-row">
              <div className="project-info">
                <div className="editable-title-container">
                  {editingTitle ? (
                    <div className="inline-edit-group">
                      <input
                        type="text"
                        className="inline-edit-input title-input"
                        value={editedTitle}
                        onChange={(e) => setEditedTitle(e.target.value)}
                        onKeyDown={handleTitleKeyDown}
                        autoFocus
                        id="edit-project-name"
                      />
                      <button
                        className="inline-edit-btn save-btn"
                        onClick={handleSaveTitle}
                        title="Save (Enter)"
                      >
                        ✓
                      </button>
                      <button
                        className="inline-edit-btn cancel-btn"
                        onClick={handleCancelEditTitle}
                        title="Cancel (Esc)"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <div className="title-display-group">
                      <h1
                        className="project-name"
                        id="project-name"
                        name="project-name"
                      >
                        {project.name}
                      </h1>
                      <button
                        className="edit-icon-btn"
                        onClick={handleStartEditTitle}
                        title="Edit title"
                      >
                        <EditIcon />
                      </button>
                    </div>
                  )}
                </div>

                <div className="editable-description-container">
                  {editingDescription ? (
                    <div className="inline-edit-group">
                      <textarea
                        className="inline-edit-input description-input"
                        value={editedDescription}
                        onChange={(e) => setEditedDescription(e.target.value)}
                        onKeyDown={handleDescriptionKeyDown}
                        autoFocus
                        rows="2"
                        id="edit-project-description"
                      />
                      <button
                        className="inline-edit-btn save-btn"
                        onClick={handleSaveDescription}
                        title="Save (Ctrl+Enter)"
                      >
                        ✓
                      </button>
                      <button
                        className="inline-edit-btn cancel-btn"
                        onClick={handleCancelEditDescription}
                        title="Cancel (Esc)"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <div className="description-display-group">
                      {project.description ? (
                        <>
                          <p
                            className="project-description"
                            id="project-description"
                            name="project-description"
                          >
                            {project.description}
                          </p>
                          <button
                            className="edit-icon-btn"
                            onClick={handleStartEditDescription}
                            title="Edit description"
                          >
                            <EditIcon />
                          </button>
                        </>
                      ) : (
                        <button
                          className="add-description-btn"
                          onClick={handleStartEditDescription}
                        >
                          + Add description
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="project-top-actions">
                {user && (
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowShareModal(true)}
                    id="share-button"
                    name="share-button"
                    aria-label="Share project with others"
                    type="button"
                  >
                    Share
                  </button>
                )}
                <button
                  className="btn btn-secondary"
                  onClick={handleExportPDF}
                  id="export-pdf-button"
                  name="export-pdf-button"
                  aria-label={pdfExportProgress !== null ? `Exporting PDF: ${pdfExportProgress}%` : "Export project to PDF"}
                  disabled={pdfExportProgress !== null}
                  type="button"
                >
                  {pdfExportProgress !== null ? `Exporting... ${pdfExportProgress}%` : "Export PDF"}
                </button>
              </div>
            </div>

            <div className="project-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowCategoryModal(true)}
                id="manage-categories-button"
                name="manage-categories-button"
                aria-label="Manage question categories"
                type="button"
              >
                Categories
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setShowBulkModal(true)}
                id="bulk-add-button"
                name="bulk-add-button"
                aria-label="Add multiple questions at once"
                type="button"
              >
                Bulk Add Questions
              </button>
            </div>
          </div>

          <form
            onSubmit={handleCreateQuestion}
            className="inline-question-form"
          >
            <input
              type="text"
              className="inline-question-input"
              value={newQuestionContent}
              onChange={(e) => setNewQuestionContent(e.target.value)}
              placeholder="Add a new question..."
              disabled={creatingQuestion}
              id="inline-question-input"
              ref={questionInputRef}
              aria-label="Enter new question"
            />
            <select
              className="inline-category-select"
              value={newQuestionCategory}
              onChange={(e) => setNewQuestionCategory(e.target.value)}
              disabled={creatingQuestion}
              id="inline-category-select"
              aria-label="Select question category"
            >
              <option value="">Category</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
            <button
              type="submit"
              className="btn inline-add-btn"
              disabled={creatingQuestion || !newQuestionContent.trim()}
              id="inline-add-question-btn"
              name="add-question-button"
              aria-label="Add new question"
            >
              {creatingQuestion ? "Adding..." : "Add"}
            </button>
          </form>

          <div className="filter-pills">
            <button
              className={`filter-pill ${filter === "all" ? "active" : ""}`}
              onClick={() => setFilter("all")}
              id="filter-all"
              name="filter-all"
              aria-label="Show all questions"
              aria-pressed={filter === "all"}
              type="button"
            >
              All ({questions.length})
            </button>
            <button
              className={`filter-pill ${filter === "answered" ? "active" : ""}`}
              onClick={() => setFilter("answered")}
              id="filter-answered"
              name="filter-answered"
              aria-label="Show only answered questions"
              aria-pressed={filter === "answered"}
              type="button"
            >
              Answered ({stats.answered})
            </button>
            <button
              className={`filter-pill ${filter === "unanswered" ? "active" : ""}`}
              onClick={() => setFilter("unanswered")}
              id="filter-unanswered"
              name="filter-unanswered"
              aria-label="Show only unanswered questions"
              aria-pressed={filter === "unanswered"}
              type="button"
            >
              Unanswered ({stats.untouched})
            </button>
            <button
              className={`filter-pill ${filter === "in_progress" ? "active" : ""}`}
              onClick={() => setFilter("in_progress")}
              id="filter-in-progress"
              name="filter-in-progress"
              aria-label="Show questions in progress"
              aria-pressed={filter === "in_progress"}
              type="button"
            >
              In Progress ({stats.inProgress})
            </button>
            <div className="author-filter">
              <label htmlFor="author-filter-select" className="author-filter-label">Author:</label>
              <select
                id="author-filter-select"
                className="author-filter-select"
                value={authorFilter}
                onChange={(e) => setAuthorFilter(e.target.value)}
              >
                <option value="all">All</option>
                <option value="myself">Myself</option>
                <option value="others">Others</option>
              </select>
            </div>
          </div>

          {showBulkActions && (
            <div className="bulk-actions-bar">
              <div className="bulk-actions-info">
                <input
                  type="checkbox"
                  checked={
                    selectedQuestions.length === filteredQuestions.length
                  }
                  onChange={handleSelectAll}
                  id="select-all-checkbox"
                  className="bulk-checkbox"
                />
                <span className="bulk-selected-count">
                  {selectedQuestions.length} selected
                </span>
              </div>
              <div className="bulk-actions-buttons">
                <select
                  className="bulk-category-select"
                  onChange={(e) => {
                    if (e.target.value) {
                      handleBulkAssignCategory(
                        e.target.value === "none" ? null : e.target.value,
                      );
                      e.target.value = "";
                    }
                  }}
                  defaultValue=""
                >
                  <option value="" disabled>
                    Assign Category
                  </option>
                  <option value="none">No Category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={handleBulkDelete}
                >
                  Delete Selected
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setSelectedQuestions([])}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          <ErrorBoundary fallbackType="section">
            <div className="questions-section">
              {filteredQuestions.length === 0 ? (
              <div className="empty-state">
                <p>No questions found.</p>
              </div>
            ) : (
              filteredQuestions.map((question) => (
                <ErrorBoundary key={question.id} fallbackType="inline">
                  <QuestionAccordion
                    question={question}
                    projectId={projectId}
                    onCreateComment={handleCreateComment}
                    onMarkAsAnswer={handleMarkAsAnswer}
                    onDeleteComment={handleDeleteComment}
                    onDeleteQuestion={handleDeleteQuestion}
                    categories={categories}
                    isSelected={selectedQuestions.includes(question.id)}
                    onToggleSelect={handleToggleQuestion}
                    projectOwnerId={project?.user_id}
                  />
                </ErrorBoundary>
              ))
            )}
            </div>
          </ErrorBoundary>
        </div>

        <QuestionNavigation questions={filteredQuestions} />
      </main>
      {showShareModal && (
        <ShareModal
          projectId={projectId}
          onClose={() => setShowShareModal(false)}
        />
      )}
      {showBulkModal && (
        <BulkQuestionModal
          categories={categories}
          onClose={() => setShowBulkModal(false)}
          onSubmit={handleBulkCreate}
        />
      )}

      {showCategoryModal && (
        <CategoryModal
          categories={categories}
          onClose={() => setShowCategoryModal(false)}
          onCreate={handleCreateCategory}
          onDelete={handleDeleteCategory}
        />
      )}
    </div>
  );
}

export default ProjectDetail;
